import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BTN_XS } from '../../constants/buttons';
import { statusPillClass } from '../ui/StatusPill';
import { ROW_EDIT_CONTROL, FIELD_LABEL_CLASS } from '../../constants/inputs';
import { ROW_EDIT_UNSAVED_CHIP_CLASS } from '../TableRowExpansion';
import { RowReorderButtons } from './ExpandRowParts';

/**
 * THE always-editable list — Payment Types (and, until the job cost codes
 * of 2026-10-28 retired them, Ad-hoc Budget Lines)
 * (owner 2026-10-25: *"paymnet types DONT need to expand as its a very simple
 * thing. Maybe we just do a list like the allowances which can be edited at
 * all times with a single save/cancle button for the whole list"*; *"same for
 * ad hoc budget lines"*; *"i dont think we need undo, just cancel will be
 * enough"*).
 *
 * Its rules → `components/budget/CLAUDE.md` (Manage Budgets), moved there 2026-10-28.
 */
let tempKeySeq = 0;
const same = (a, b) => String(a ?? '') === String(b ?? '');

export function useEditableList(rows, fieldsOf, blank) {
  const fieldsOfRef = useRef(fieldsOf);
  fieldsOfRef.current = fieldsOf;
  const fromRows = useCallback(
    (list) => (list || []).map((r) => ({ _key: `id-${r.id}`, id: r.id, ...fieldsOfRef.current(r) })),
    [],
  );
  // 🚨 THE DRAFT IS DIFFED AGAINST THE LIST IT WAS SEEDED FROM (`base`), never
  // against the latest `rows`. Diffing against `rows` read a draft seeded
  // before the first load (empty) as "every saved row deleted" — dirty, so it
  // was never re-seeded, and the first Save deleted the whole list (caught on
  // the owner's preview copy, 2026-10-25). Pinned by EditableList.test.js §1.
  const [base, setBase] = useState(() => rows || []);
  const [draft, setDraft] = useState(() => fromRows(rows));
  const fieldKeys = Object.keys(blank);
  const isBlankNew = (d) => d.id == null && fieldKeys.every((k) => String(d[k] ?? '').trim() === '');

  const savedById = useMemo(() => new Map(base.map((r) => [r.id, r])), [base]);

  const changes = useCallback(() => {
    const kept = draft.filter((d) => !isBlankNew(d));
    const keptIds = new Set(kept.filter((d) => d.id != null).map((d) => d.id));
    const creates = [];
    const updates = [];
    // 🔑 ORDER IS RELATIVE. Stored sort_orders often have gaps (deletions,
    // imports) — comparing them with list positions read an untouched list as
    // "moved" (dirty on load; first Save renumbered every row — test-agent
    // finding 2026-10-25). Renumber ONLY when the user changed the order: the
    // saved rows' sequence differs, or a new row sits above a saved one.
    const keptSavedIds = kept.filter((d) => d.id != null).map((d) => d.id);
    const baseKeptIds = base.map((r) => r.id).filter((id) => keptIds.has(id));
    const lastSavedIdx = kept.reduce((m, d, i) => (d.id != null ? i : m), -1);
    const reordered = keptSavedIds.join(',') !== baseKeptIds.join(',')
      || kept.some((d, i) => d.id == null && i < lastSavedIdx);
    let nextOrder = base.reduce((m, r) => Math.max(m, Number(r.sort_order) || 0), -1) + 1;
    kept.forEach((d, position) => {
      if (d.id == null) {
        creates.push({ ...d, sort_order: reordered ? position : nextOrder++ });
        return;
      }
      const saved = savedById.get(d.id);
      const fields = fieldsOfRef.current(saved);
      const sortOrder = reordered ? position : Number(saved.sort_order);
      const moved = reordered && Number(saved.sort_order) !== position;
      if (moved || fieldKeys.some((k) => !same(fields[k], d[k]))) updates.push({ ...d, sort_order: sortOrder });
    });
    const deletes = base.filter((r) => !keptIds.has(r.id));
    return { creates, updates, deletes };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft, base, savedById]);

  const { creates, updates, deletes } = changes();
  const dirty = creates.length + updates.length + deletes.length > 0;

  // A new saved list (load, save, version switch) re-seeds a CLEAN draft; a
  // dirty draft is never overwritten under the user.
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;
  useEffect(() => {
    if (!dirtyRef.current) { setBase(rows || []); setDraft(fromRows(rows)); }
  }, [rows, fromRows]);

  return {
    draft,
    dirty,
    changes,
    setField: (key, field, value) => setDraft((prev) => prev.map((d) => (d._key === key ? { ...d, [field]: value } : d))),
    add: () => setDraft((prev) => [...prev, { _key: `new-${++tempKeySeq}`, id: null, ...blank }]),
    remove: (key) => setDraft((prev) => prev.filter((d) => d._key !== key)),
    move: (idx, dir) => setDraft((prev) => {
      const j = idx + dir;
      if (j < 0 || j >= prev.length) return prev;
      const next = [...prev];
      [next[idx], next[j]] = [next[j], next[idx]];
      return next;
    }),
    reset: (list = base) => { setBase(list || []); setDraft(fromRows(list)); },
  };
}

const DELETE_BTN =
  'w-7 h-7 inline-flex items-center justify-center rounded-md text-gray-400 dark:text-gray-400 transition-colors hover:bg-red-50 dark:hover:bg-red-900/30 hover:text-red-600 dark:hover:text-red-400 focus:outline-none focus:ring-2 focus:ring-red-500';

/**
 * columns: [{ key, label, width (CSS grid track), align?, placeholder?,
 *   render?: ({ value, onChange, row, testId }) => node }] — default is a text box.
 */
export default function EditableList({ list, columns, onSave, saving, testId, rowName, footer }) {
  const grid = { gridTemplateColumns: `${columns.map((c) => c.width).join(' ')} 40px 32px` };
  return (
    <div className="max-w-[56rem]" data-testid={testId}>
      {/* 📱 A phone scrolls the LIST sideways in its own box, never the page. */}
      <div className="overflow-x-auto">
      <div className="grid gap-x-2.5 gap-y-1.5 items-center min-w-[32rem]" style={grid}>
        {columns.map((c) => (
          <div key={c.key} className={`${FIELD_LABEL_CLASS} mb-0 ${c.align === 'right' ? 'text-right' : ''}`}>{c.label}</div>
        ))}
        <div /><div />
        {list.draft.map((d, idx) => (
          <React.Fragment key={d._key}>
            {columns.map((c) => {
              const tid = `${testId}-${idx}-${c.key}`;
              const onChange = (v) => list.setField(d._key, c.key, v);
              return (
                <div key={c.key} className="min-w-0">
                  {c.render ? c.render({ value: d[c.key], onChange, row: d, testId: tid }) : (
                    <input
                      type="text"
                      className={`${ROW_EDIT_CONTROL} ${c.align === 'right' ? 'text-right' : ''}`}
                      value={d[c.key] ?? ''}
                      placeholder={c.placeholder}
                      aria-label={`${c.label} — ${rowName(d) || 'new row'}`}
                      onChange={(e) => onChange(e.target.value)}
                      data-testid={tid}
                    />
                  )}
                </div>
              );
            })}
            <div className="flex justify-center">
              <RowReorderButtons index={idx} count={list.draft.length} onMove={list.move} />
            </div>
            <div className="flex justify-center">
              <button
                type="button"
                className={DELETE_BTN}
                title="Delete (applies when you Save)"
                aria-label={`Delete ${rowName(d) || 'new row'}`}
                onClick={() => list.remove(d._key)}
                data-testid={`${testId}-${idx}-delete`}
              >
                ✕
              </button>
            </div>
          </React.Fragment>
        ))}
      </div>
      </div>
      {footer}
      <div className="flex items-center justify-end gap-2 mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
        {list.dirty && (
          <span className={`inline-flex ${statusPillClass()} ${ROW_EDIT_UNSAVED_CHIP_CLASS}`} data-testid={`${testId}-dirty`}>
            ⚠ Unsaved changes
          </span>
        )}
        <button type="button" className={BTN_XS.secondary} disabled={!list.dirty || saving} onClick={() => list.reset()} data-testid={`${testId}-cancel`}>
          Cancel
        </button>
        <button type="button" className={BTN_XS.confirm} disabled={!list.dirty || saving} onClick={onSave} data-testid={`${testId}-save`}>
          {saving ? 'Saving…' : 'Save changes'}
        </button>
      </div>
    </div>
  );
}
